import React, { useEffect, useState } from 'react';
import { Toast as ToastType } from '../types';

interface ToastProps {
  toast: ToastType;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(true);

  useEffect(() => {
    // Trigger entrance animation
    const enterTimer = setTimeout(() => setIsEntering(false), 50);
    
    const duration = toast.duration || 5000;
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onClose(toast.id), 300);
    }, duration);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
    };
  }, [toast, onClose]);

  const getToastStyles = () => {
    // Dark theme styling with semantic colors
    const baseStyles = 'px-6 py-4 rounded-xl shadow-md flex items-start gap-3 min-w-[320px] max-w-[480px] backdrop-blur-sm border';
    
    // Smooth slide-in from right animation (300ms ease-out)
    const animationStyles = isExiting 
      ? 'opacity-0 translate-x-full transition-all duration-normal ease-in' 
      : isEntering
      ? 'opacity-0 translate-x-8'
      : 'opacity-100 translate-x-0 transition-all duration-normal ease-out';

    switch (toast.type) {
      case 'success':
        return `${baseStyles} ${animationStyles} bg-light-surface border-light-semantic-success-subtle text-light-text-primary`;
      case 'error':
        return `${baseStyles} ${animationStyles} bg-light-surface border-light-semantic-error-subtle text-light-text-primary`;
      case 'warning':
        return `${baseStyles} ${animationStyles} bg-light-surface border-light-semantic-warning-subtle text-light-text-primary`;
      case 'info':
      default:
        return `${baseStyles} ${animationStyles} bg-light-surface border-light-semantic-info-subtle text-light-text-primary`;
    }
  };

  const getIcon = () => {
    // Dark theme semantic color icons
    const iconClass = 'w-5 h-5 flex-shrink-0 mt-0.5';
    
    switch (toast.type) {
      case 'success':
        return (
          <svg className={`${iconClass} text-light-semantic-success`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className={`${iconClass} text-light-semantic-error`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className={`${iconClass} text-light-semantic-warning`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className={`${iconClass} text-light-semantic-info`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Use assertive for errors and warnings, polite for success and info
  const ariaLive = toast.type === 'error' || toast.type === 'warning' ? 'assertive' : 'polite';

  return (
    <div className={getToastStyles()} role="alert" aria-live={ariaLive} aria-atomic="true">
      {getIcon()}
      <p className="flex-1 text-body-sm font-medium text-light-text-primary">{toast.message}</p>
      <button
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => onClose(toast.id), 300);
        }}
        className="flex-shrink-0 text-light-text-tertiary hover:text-light-text-primary transition-all duration-normal hover:scale-110"
        aria-label="Close notification"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

export default Toast;
